import { Wand2 } from "lucide-react";
import { useEffect, useState } from "react";
import Button from "@/components/ui/Button";
import Checkbox from "@/components/ui/Checkbox";
import Input from "@/components/ui/Input";
import Modal from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import type { Retailer } from "@/pages/admin/retailers/types";

interface ResetPasswordModalProps {
  open: boolean;
  onClose: () => void;
  retailer: Retailer | null;
  onReset: (id: string, password: string, notify: boolean) => Promise<void>;
}

function generatePassword(): string {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
  let out = "";
  for (let i = 0; i < 10; i += 1) {
    out += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return out;
}

export default function ResetPasswordModal({ open, onClose, retailer, onReset }: ResetPasswordModalProps) {
  const toast = useToast();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [notify, setNotify] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setPassword("");
      setConfirm("");
      setNotify(true);
      setErrors({});
    }
  }, [open]);

  function validate(): boolean {
    const e: Record<string, string> = {};
    if (!password) e.password = "Password is required";
    else if (password.length < 8) e.password = "Password must be at least 8 characters";
    if (!confirm) e.confirm = "Please confirm the password";
    else if (confirm !== password) e.confirm = "Passwords do not match";
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleReset() {
    if (!validate() || !retailer) return;
    setSaving(true);
    try {
      await onReset(retailer.id, password, notify);
      onClose();
    } catch (err: any) {
      toast.error(err?.message || "Failed to reset password");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Reset password"
      footer={
        <div className="flex items-center justify-end gap-2.5 border-t border-line px-5 py-4">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleReset} loading={saving}>
            Reset Password
          </Button>
        </div>
      }
    >
      <p className="text-sm text-ink-secondary">
        A new password will be set for <span className="font-medium text-ink">{retailer?.shopName}</span> (
        {retailer?.id}). The retailer must be informed of the new password.
      </p>

      <div className="mt-5 space-y-4">
        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <span className="text-[13px] font-medium text-ink-secondary">New Password</span>
            <button
              type="button"
              onClick={() => setPassword(generatePassword())}
              className="inline-flex items-center gap-1 text-xs font-medium text-primary-500 hover:text-primary-600 cursor-pointer"
            >
              <Wand2 className="h-3.5 w-3.5" />
              Generate
            </button>
          </div>
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={errors.password}
            placeholder="Enter a new password"
          />
        </div>
        <Input
          type="password"
          label="Confirm Password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          error={errors.confirm}
          placeholder="Re-enter the new password"
        />
        <Checkbox
          checked={notify}
          onChange={(e) => setNotify(e.target.checked)}
          label="Send the new password to the retailer via SMS and email"
        />
      </div>
    </Modal>
  );
}